// useAuthors — resolves author_id -> display info (name, initials, division).
//
// The /feed and /projects endpoints return only author_id. The /leaderboard
// endpoint already returns every member's name/initials/division/role, so we
// reuse it as a live directory lookup rather than hardcoding the design's
// mock member table.
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";
import type { AvatarConfig } from "@/features/avatar/catalog";

export interface AuthorInfo {
  member_id: string;
  name: string;
  initials: string;
  division: string;
  role: string;
  avatar?: AvatarConfig | null;
}

interface LeaderboardEntry extends AuthorInfo {
  rank: number;
  reputation: number;
  avatar: AvatarConfig;
}

interface LeaderboardResponse {
  entries: LeaderboardEntry[];
}

export type AuthorMap = Record<string, AuthorInfo>;

export function useAuthors(): AuthorMap {
  const [map, setMap] = useState<AuthorMap>({});

  useEffect(() => {
    let cancelled = false;
    apiClient<LeaderboardResponse>("/api/v1/leaderboard")
      .then((res) => {
        if (cancelled) return;
        const next: AuthorMap = {};
        for (const e of res.entries) {
          next[e.member_id] = {
            member_id: e.member_id,
            name: e.name,
            initials: e.initials,
            division: e.division,
            role: e.role,
            avatar: e.avatar,
          };
        }
        setMap(next);
      })
      .catch(() => {
        // Best-effort: feed rows fall back to author_id-derived avatars.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return map;
}
